/**
 * The shape of the tunable game settings, and the rules for what counts as a valid value.
 *
 * This file holds the **schema only** — no values are read from disk here, so it imports
 * nothing and compiles for the browser, for Node, and for the editor alike. The stored
 * values live in `config/*.json` and are loaded by [Config.ts](Config.ts); the editor
 * (`editor/`) renders its panels straight off the field metadata below, so a setting that
 * is added here appears in the dashboard without any UI being written for it.
 *
 * See design/Settings.md — that document and this file are a contract.
 */

/**
 * The `General` panel: the handful of numbers that decide how big the sea is and how a hull
 * moves through it. One interface per panel, one JSON file per panel.
 */
export interface GeneralSettings {
    /** Width and height of the square, looping arena, in world pixels. */
    mapSize: number;
    /** Cruise speed of every hull, in pixels per second. */
    boatBaseSpeed: number;
    /**
     * Extra speed while the turbo is lit, in pixels per second, **added** to the base speed.
     * Additive rather than a multiplier so the dashboard reads in one unit; the multiplier
     * the design docs talk about is derived from it.
     */
    turboSpeedIncrement: number;
    /**
     * How much of a hull's speed is paid for in manoeuvrability. A hull making `s` times its
     * own cruise speed keeps `1 / (1 + turnSpeedFactor * (s - 1))` of its turn rate, so **zero
     * is the arena as it was before the dial existed** — speed cost nothing but the widening
     * circle that comes free of covering more ground per radian — and one is a hull under the
     * default turbo turning at 0.56x, near enough half.
     */
    turnSpeedFactor: number;
    /**
     * How hard the wind blows, in pixels per second. **Constant** — only the wind's direction
     * changes, so this is the whole of its strength. Expressed in the same unit a hull's speed
     * is, because that is what a sailing system will eventually weigh it against.
     */
    windSpeed: number;
    /**
     * The **half-width of the dead sector**, in degrees, measured from the eye of the wind: how
     * close to the wind a hull may point before its canvas draws nothing at all. 45 makes a
     * quarter of the compass unsailable.
     *
     * One number produces the whole point-of-sail curve — see `sailPower` in `shared/Wind.ts`.
     * Zero leaves only the exact head-to-wind heading dead; 90 kills everything forward of the
     * beam.
     */
    windNoGoAngle: number;
    /**
     * How fast the wind veers toward the quarter it is chasing, in **degrees** per second.
     * Degrees rather than radians because this is a dial somebody drags, and nobody has an
     * intuition for 0.05 rad/s; `shared/Wind.ts` converts it once on the way in.
     */
    windVeerRate: number;
    /**
     * Mean seconds between the wind picking a new quarter to chase. The actual gap is jittered
     * around it, so the sea never turns on a metronome — see `shared/Wind.ts`.
     */
    windShiftInterval: number;
    /** Points burned per second of turbo. */
    turboCost: number;
    /** What every hull joins with, and what a sunk one comes back with. */
    startingScore: number;
    /**
     * The floor a hull's score is never burned below: the turbo cuts out at or below this.
     * A captain can spend most of a ship chasing a kill, but never the last of it.
     */
    minimumBoatScore: number;
    /**
     * The death line. A hull holding **less** than this is sunk outright by the next ball to
     * find it, and the whole hold goes to whoever fired — see design/Battle.md. At or above it,
     * a hit is the ordinary percentage steal and the ship sails on.
     *
     * **Zero switches death off**, which is the arena as it was before the dial existed: a hit
     * only ever costs points, and no hull is ever removed from the sea by one.
     */
    deathScore: number;
    /**
     * How long a sunk captain stays at sea watching the wreck before the scene changes, in
     * seconds. **The one key on this panel the arena never reads** — the hull is already out of
     * `state.ships` and the room holds nothing but the seat, so this is purely how long the
     * client waits before putting its player ashore. It lives here rather than in a client-only
     * panel because it is the other half of the death rule and belongs beside `deathScore`;
     * two captains lingering for different counts is not a desync.
     *
     * Zero puts them ashore on the frame their hull leaves the sea, which is the game as it was
     * before the dial existed. See design/Battle.md.
     */
    deathLingerSeconds: number;
    /**
     * Doubloons a second at the **top of the leaderboard**, before the falloff below thins it out
     * for everybody else. The headline number of the whole economy: what holding first place is
     * worth per second of holding it. See design/Shop.md.
     */
    purseTopRate: number;
    /**
     * How steeply the purse thins with rank, as an exponent: `topRate / rank ^ falloff`. At zero
     * every rank pays the same and the leaderboard decides nothing; at one it is a plain
     * reciprocal and tenth place earns a tenth of first; above one only the very top is worth
     * holding.
     */
    purseFalloff: number;
    /**
     * Doubloons a second every living ship earns regardless of rank.
     *
     * **The only thing standing between a new player and a shop they can never use.** A pure rank
     * economy pays the people who are already winning, which in a game whose shop makes you better
     * is a spiral with one direction. Zero switches it off, which is a real option and is why it
     * is a dial rather than a constant.
     */
    purseFloorRate: number;
}

/**
 * What the editor needs to draw one row, and what the `coerce*` functions need to police it.
 *
 * Generic in its key so one row component serves every panel: `SettingField` on its own is
 * "a row for some panel", which is what `editor/components/SettingRow.tsx` wants, while a
 * panel's own array is narrowed to that panel's keys so a typo is a build error.
 */
export interface SettingField<Key extends string = string> {
    key: Key;
    /**
     * Heading the row sits under in the dashboard. Rows are drawn in array order and a new
     * heading is emitted whenever this changes, so grouping is positional — keep the fields
     * of one group adjacent.
     */
    group: string;
    /** Human label for the dashboard row. */
    label: string;
    /** Unit suffix shown after the number, e.g. `px/s`. Empty for a bare count. */
    unit: string;
    min: number;
    max: number;
    /** Slider granularity. Values are clamped to the range but never snapped to the step. */
    step: number;
    /**
     * What the dashboard draws. Left out — the usual case — it is a slider and a box.
     *
     * `"toggle"` is a checkbox, for the handful of dials that are only ever on or off. It is
     * still **stored as a number**, 0 or 1, and still carries a `min`, `max` and `step`, so the
     * whole path from `coercePanel` to the write API to `Record<Key, number>` in the editor
     * stays exactly one shape. A boolean in the JSON would be a second kind of setting for the
     * sake of two characters on disk.
     */
    kind?: "number" | "toggle";
    /** One sentence of why this number exists, shown under the row. */
    description: string;
}

/**
 * A stored **colour**, and the second kind of field in the dashboard.
 *
 * It is separate from `SettingField` rather than another `kind` on it because a colour has no
 * range, no step and nothing to clamp: everything `SettingField` carries would be dead weight,
 * and `coercePanel`'s one job — put this number between these two — has no meaning here.
 *
 * The stored value is a **`"#rrggbb"` string**, not a packed number. JSON has no hex literal, so
 * a number would put `9498868` in a checked-in file that people are expected to read, and it is
 * the one representation `<input type="color">` already speaks.
 */
export interface ColorField<Key extends string = string> {
    key: Key;
    /** Heading the row sits under, exactly as `SettingField.group`. */
    group: string;
    label: string;
    /** One sentence of why this colour exists, shown under the swatch. */
    description: string;
}

/** A colour split into its three channels, 0–255. */
export interface Rgb {
    r: number;
    g: number;
    b: number;
}

/** Whether a string is a colour this schema will store. Lower case, six digits, with the hash. */
const HEX = /^#[0-9a-f]{6}$/;

/** `"#0090f4"` as three channels. Anything unparseable is black, which no default ever is. */
export function rgbOf(hex: string): Rgb {
    const value = Number.parseInt(hex.slice(1), 16);
    return Number.isFinite(value) ? { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff } : { r: 0, g: 0, b: 0 };
}

/** The same colour as the packed number Pixi wants for a tint. */
export function packColor(hex: string): number {
    const { r, g, b } = rgbOf(hex);
    return (r << 16) | (g << 8) | b;
}

/** A packed `0xrrggbb` back as `"#rrggbb"` — what `<input type="color">` reads and writes. */
export function hexOf(packed: number): string {
    return `#${(packed & 0xffffff).toString(16).padStart(6, "0")}`;
}

/**
 * A packed colour split into its three channels. The counterpart of `rgbOf` for the numbers on
 * the wire, which carry a colour as one `uint32` rather than as six characters.
 */
export function channelsOf(packed: number): Rgb {
    const value = Number.isFinite(packed) ? Math.trunc(packed) & 0xffffff : 0;
    return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

/**
 * **HSL lightness**, 0 to 1: the midpoint of the brightest and dimmest channel.
 *
 * Not luminance, deliberately. This is the measure the boat pack's material ramps are normalised
 * on (design/Art.md §2) and the one `Cosmetics.sailLightnessFloor` is written
 * against, and two different meanings of "how dark is this" would put the floor in a different
 * place from the ramp it is meant to guard.
 */
export function lightnessOf(packed: number): number {
    const { r, g, b } = channelsOf(packed);
    return (Math.max(r, g, b) + Math.min(r, g, b)) / 2 / 255;
}

/** A colour as a picker thinks of it: hue in degrees, saturation and lightness 0 to 1. */
export interface Hsl {
    h: number;
    s: number;
    l: number;
}

/**
 * A packed colour as hue, saturation and lightness — the three axes a person actually picks a
 * colour on, and the space `sailLightnessFloor` is written in.
 *
 * `l` is the same measure `lightnessOf` returns, which is what makes the floor expressible as a
 * slider's `min`: a control that cannot go below it cannot offer a colour the arena would correct.
 */
export function hslOf(packed: number): Hsl {
    const { r, g, b } = channelsOf(packed);
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const l = (max + min) / 2;
    const chroma = max - min;
    if (chroma === 0) return { h: 0, s: 0, l };

    const s = chroma / (1 - Math.abs(2 * l - 1));
    const [red, green, blue] = [r / 255, g / 255, b / 255];
    const h = max === red ? ((green - blue) / chroma) % 6 : max === green ? (blue - red) / chroma + 2 : (red - green) / chroma + 4;

    return { h: ((h * 60) % 360 + 360) % 360, s, l };
}

/** The inverse. Lightness comes back out of `lightnessOf` exactly, which the floor depends on. */
export function packHsl({ h, s, l }: Hsl): number {
    const chroma = (1 - Math.abs(2 * l - 1)) * s;
    const hue = (((h % 360) + 360) % 360) / 60;
    const second = chroma * (1 - Math.abs((hue % 2) - 1));
    const base = l - chroma / 2;

    const [r, g, b] =
        hue < 1 ? [chroma, second, 0]
        : hue < 2 ? [second, chroma, 0]
        : hue < 3 ? [0, chroma, second]
        : hue < 4 ? [0, second, chroma]
        : hue < 5 ? [second, 0, chroma]
        : [chroma, 0, second];

    const channel = (value: number) => Math.round((value + base) * 255);
    return (channel(r) << 16) | (channel(g) << 8) | channel(b);
}

/**
 * The same colour, lifted until its lightness reaches `floor`. A colour already at or above it
 * is returned unchanged, so this is idempotent and can be applied wherever a colour arrives.
 *
 * It **blends toward white** rather than scaling the channels up. Scaling preserves saturation
 * but overflows — a floor of 0.9 on a saturated colour would need channels past 255 and would
 * clip one of them, turning the hue on the way. Blending is exact at every floor: mixing toward
 * white moves the brightest and dimmest channel by the same fraction of their distance to 255,
 * so lightness lands on the floor and nothing can clip. A sail forced up reads as a paler version
 * of the colour that was asked for, which is the honest answer to "too dark to see".
 */
export function raiseLightness(packed: number, floor: number): number {
    const lightness = lightnessOf(packed);
    if (!(floor > lightness)) return Math.trunc(packed) & 0xffffff;
    if (floor >= 1) return 0xffffff;

    const towardWhite = (floor - lightness) / (1 - lightness);
    const { r, g, b } = channelsOf(packed);
    // Rounded **up**, not to nearest. A floor of 0.35 wants a channel at 89.25, and rounding that
    // to 89 lands on 0.3490 — under the floor the whole thing exists to guarantee. Ceiling costs
    // at most one level per channel, which is below anything an eye resolves.
    const lift = (channel: number) => Math.min(255, Math.ceil(channel + (255 - channel) * towardWhite));
    return (lift(r) << 16) | (lift(g) << 8) | lift(b);
}

/**
 * What `base` has to be **multiplied by** to land on `target`, per channel.
 *
 * The sea's depth ramp is multiplicative on purpose — see `DEEP_TINT` in
 * [OceanFilter.ts](../src/scripts/sea/OceanFilter.ts) — but a multiplier is not a thing anybody
 * can picture, so the dashboard stores the colour the water should *read as* and this turns it
 * back into the number the shader wants. One implementation, used by the renderer and by the
 * panel's readouts alike.
 *
 * **A channel the base has none of cannot be reached**, and is answered with 1 rather than a
 * division by zero: no multiplier puts red into a colour that has none. The panel says so.
 */
export function tintBetween(base: string, target: string): Rgb {
    const from = rgbOf(base);
    const to = rgbOf(target);
    const ratio = (a: number, b: number) => (a === 0 ? 1 : b / a);

    return { r: ratio(from.r, to.r), g: ratio(from.g, to.g), b: ratio(from.b, to.b) };
}

/**
 * The colour half of a panel's coercion: anything that is not a `"#rrggbb"` string falls back to
 * the default for that key. Runs after `coercePanel`, over the same object.
 *
 * Case is normalised on the way in, so a hand-typed `#0090F4` is stored the way every other
 * colour in the file is written rather than as a second spelling of it.
 */
function coerceColors<Settings>(fields: ColorField<keyof Settings & string>[], out: Settings, raw: unknown): Settings {
    const source = (raw ?? {}) as Partial<Record<keyof Settings, unknown>>;

    for (const field of fields) {
        const value = source[field.key];
        if (typeof value !== "string") continue;

        const hex = value.trim().toLowerCase();
        if (HEX.test(hex)) out[field.key] = hex as Settings[keyof Settings & string];
    }

    return out;
}

/**
 * The share of cruise a hull keeps under bare poles however hard `windSpeed` is pushed —
 * `BARE_SHARE_FLOOR` in [Wind.ts](Wind.ts), copied here for the same reason `SWELL_DISPLACEMENT`
 * is copied further down: this file may not import the modules that read it, and a readout has
 * to answer about the sliders rather than about the saved file.
 */
const BARE_SHARE = 0.15;

/**
 * The values the game ships with, and the fallback for anything the stored JSON gets wrong.
 *
 * **These are `config/general.json`, and they are meant to stay that way.** They drifted apart
 * once — five of them, by as much as `lootValue` being three times what this object claimed —
 * and nothing caught it, because every key is present in the file so the fallback never fires.
 * What it broke was every sentence in design/ that quotes a default, which is most of them.
 * An audit on 2026-08-30 realigned all three — this object, the file, and the table in
 * design/Settings.md — and found nineteen values across three panels where they disagreed. It is
 * a dozen lines and worth re-running whenever a default is edited by hand.
 */
export const GENERAL_DEFAULTS: GeneralSettings = {
    mapSize: 12000,
    boatBaseSpeed: 110,
    turboSpeedIncrement: 88,
    turnSpeedFactor: 1.2,
    windSpeed: 60,
    windNoGoAngle: 45,
    windVeerRate: 2,
    windShiftInterval: 20,
    turboCost: 30,
    startingScore: 100,
    minimumBoatScore: 20,
    deathScore: 200,
    deathLingerSeconds: 5,
    purseTopRate: 6,
    purseFalloff: 0.7,
    purseFloorRate: 0.5
};

export const GENERAL_FIELDS: SettingField<keyof GeneralSettings>[] = [
    {
        key: "mapSize",
        group: "The sea",
        label: "Map size",
        unit: "px",
        min: 2000,
        max: 40000,
        step: 100,
        description:
            "Width and height of the square arena. The world is a torus, so this is not a wall — it is how far apart two boats can get. Boats are drawn through the seam, which only works while the viewport is narrower than half of this."
    },
    {
        key: "boatBaseSpeed",
        group: "Sailing",
        label: "Boat base speed",
        unit: "px/s",
        min: 10,
        max: 600,
        step: 5,
        description: "Cruise speed of every hull. Raising it without raising the map size makes the sea feel smaller."
    },
    {
        key: "turboSpeedIncrement",
        group: "Sailing",
        label: "Turbo speed increment",
        unit: "px/s",
        min: 0,
        max: 800,
        step: 1,
        description:
            "Added on top of the base speed while the turbo is lit. A bigger increment widens the turning circle on its own, and costs turn rate on top of that once the speed/turn factor is above zero."
    },
    {
        key: "turnSpeedFactor",
        group: "Sailing",
        label: "Speed costs turning",
        unit: "×",
        min: 0,
        max: 2,
        step: 0.05,
        description:
            "How much of a hull's speed is paid for in manoeuvrability. Zero is the old arena — going faster only widened the circle, it never slowed the rudder. One takes a hull at default turbo down to 0.56× its cruise turn rate, so the circle widens by speed and by rudder together."
    },
    {
        key: "windSpeed",
        group: "Wind",
        label: "Wind speed",
        unit: "px/s",
        min: 0,
        max: 400,
        step: 5,
        description:
            "How hard the wind blows. It is constant — only the direction changes — and nothing reads it yet: the sails that will are design/Evolution.md's next track. Read it against the base speed, since that is what a hull under canvas will be weighed against. Zero is a dead calm, which leaves the vane pointing at nothing in particular."
    },
    {
        key: "windNoGoAngle",
        group: "Wind",
        label: "No-go angle",
        unit: "°",
        min: 0,
        max: 90,
        step: 1,
        description:
            "How close to the eye of the wind a hull may point before its sails stop drawing, measured from dead upwind. It is the half-width of the dead sector, so 45 makes a quarter of the compass unsailable and is the first number to soften if the sea feels sticky. It also sets the whole shape of the curve either side of it: everything abaft that limit draws something, and the closer to the wind the less."
    },
    {
        key: "windVeerRate",
        group: "Wind",
        label: "Wind veer rate",
        unit: "°/s",
        min: 0,
        max: 45,
        step: 0.5,
        description:
            "How fast the wind swings toward the new quarter it is chasing. This is the dial that decides whether the weather is something a captain plans around or something that happens to them: slow enough and a course held for a minute is still a good course, fast enough and who is favoured in a chase reverses halfway through it. It is also what the sea follows: the waves swing round toward wherever the wind has got to, at the pace the wave turn sets. Zero pins the wind where the arena started it."
    },
    {
        key: "windShiftInterval",
        group: "Wind",
        label: "Wind shift interval",
        unit: "s",
        min: 1,
        max: 300,
        step: 1,
        description:
            "Average seconds between the wind choosing a new quarter to chase. Jittered around this, so shifts never land on a beat. It rarely arrives before the next one is picked at the default veer rate, which is what makes the wind wander continuously rather than swing and then sit still."
    },
    {
        key: "turboCost",
        group: "Score",
        label: "Turbo cost",
        unit: "pts/s",
        min: 0,
        max: 200,
        step: 5,
        description:
            "What a second of turbo costs, out of the only number a hull has. Keep it equal to the loot value: one coin buys one second is a sum a player can hold in their head while sailing, and it is also what makes the spill a coin a second — a legible line of them across the water rather than a dotted one. It is the price of the answer to a bad wind, which is what it mostly gets spent on: in irons the turbo is worth nearly three times what it is worth running, so it has to cost enough that escaping the eye of the wind is a decision rather than a reflex."
    },
    {
        key: "startingScore",
        group: "Score",
        label: "Starting score",
        unit: "pts",
        min: 1,
        max: 5000,
        step: 10,
        description:
            "What every hull joins with. Not zero on purpose: a fresh boat has to be worth shooting at, or newcomers get ignored rather than fought. It is also what the first hull tier is priced against."
    },
    {
        key: "minimumBoatScore",
        group: "Score",
        label: "Minimum boat score",
        unit: "pts",
        min: 0,
        max: 1000,
        step: 1,
        description:
            "The turbo cuts out at or below this score, so there is always a ship left to fight with. A score of zero would make the minimum plunder meaningless."
    },
    {
        key: "deathScore",
        group: "Score",
        label: "Death threshold",
        unit: "pts",
        min: 0,
        max: 5000,
        step: 10,
        description:
            "A hull holding less than this is sunk by the next ball that finds it, and the attacker takes the whole hold; at or above it a hit is the ordinary percentage steal. This is the number that turns a long chase into a kill, so judge it against the starting score — set above it and every fresh boat dies to one shot. Zero switches death off."
    },
    {
        key: "deathLingerSeconds",
        group: "Score",
        label: "Watch the wreck for",
        unit: "s",
        min: 0,
        max: 30,
        step: 0.5,
        description:
            "How long a sunk captain stays at sea after their hull goes down, watching it settle and whoever sank it sail on, before the menu comes back. A life used to end by the screen changing on the frame the ball landed, which gave a sinking no moment at all. Long enough to read what happened, short enough that nobody is kept from their next battle; zero restores the instant cut."
    },
    {
        key: "purseTopRate",
        group: "Purse",
        label: "Rank 1 pays",
        unit: "dbl/s",
        min: 0,
        max: 100,
        step: 0.5,
        description:
            "Doubloons a second for holding first place — the headline number of the whole economy. A captain earns for the rank they hold, for as long as they hold it, and banks it when the life ends however it ends. Nothing about it can be spent, traded or gambled inside a battle."
    },
    {
        key: "purseFalloff",
        group: "Purse",
        label: "Rank falloff",
        unit: "exp",
        min: 0,
        max: 2,
        step: 0.05,
        description:
            "How steeply the purse thins down the board, as an exponent on the rank. Zero pays every rank the same and makes the leaderboard decide nothing; one is a plain reciprocal, so tenth place earns a tenth of first; above one, only the very top is worth holding."
    },
    {
        key: "purseFloorRate",
        group: "Purse",
        label: "Everyone earns",
        unit: "dbl/s",
        min: 0,
        max: 20,
        step: 0.1,
        description:
            "Doubloons a second every living ship earns whatever their rank. The only thing standing between a new player and a shop they can never reach: a pure rank economy pays the people who are already winning, which in a game whose shop makes you better is a spiral with one direction. Zero switches it off."
    }
];

/**
 * Turns anything at all into a usable panel object: unknown keys are dropped, missing and
 * non-finite ones fall back to the default, and everything else is clamped into its field's
 * range.
 *
 * Both ends call it. The editor's API runs it before writing, so a bad edit cannot be
 * stored; the game runs it on load, so a hand-edited or stale JSON file cannot put a `NaN`
 * into the simulation — where `@colyseus/schema` would encode it as `0` and quietly teleport
 * every hull to the origin.
 *
 * Fields are policed **one at a time**. There is deliberately no cross-field rule here: a
 * clamp that read a second key would fight the slider under the cursor, so a panel with a
 * min above its max is stored as asked and sorted out by whoever reads it.
 */
function coercePanel<Settings>(fields: SettingField<keyof Settings & string>[], defaults: Settings, raw: unknown): Settings {
    const source = (raw ?? {}) as Partial<Record<keyof Settings, unknown>>;
    const out = { ...defaults };

    for (const field of fields) {
        const value = Number(source[field.key]);
        if (!Number.isFinite(value)) continue;

        const clamped = Math.min(field.max, Math.max(field.min, value));
        // A toggle is stored as a number but only ever means on or off, so a hand-edited 0.5 is
        // rounded rather than kept — the alternative is a checkbox that is neither.
        out[field.key] = (field.kind === "toggle" ? Math.round(clamped) : clamped) as Settings[keyof Settings & string];
    }

    return out;
}

/** Indexed lookup, so a panel and its coercion do not both scan the array. */
function indexFields<Key extends string>(fields: SettingField<Key>[]): Map<Key, SettingField<Key>> {
    return new Map(fields.map((field) => [field.key, field]));
}

const GENERAL_BY_KEY = indexFields(GENERAL_FIELDS);

/** Anything at all, as a usable `GeneralSettings`. See `coercePanel`. */
export function coerceGeneral(raw: unknown): GeneralSettings {
    return coercePanel(GENERAL_FIELDS, GENERAL_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function generalField(key: keyof GeneralSettings): SettingField<keyof GeneralSettings> {
    return GENERAL_BY_KEY.get(key)!;
}

/**
 * Numbers that are not stored because they fall out of the ones that are. The editor shows
 * them as read-only readouts next to the sliders: the point of the panel is judging a
 * change, and "the sea is now 40 seconds across" is the part a designer actually feels.
 */
export interface GeneralDerived {
    /** How long it takes to sail the full width of the arena at cruise, in seconds. */
    crossingSeconds: number;
    /**
     * What a **bare-poled reference hull** makes on each point of sail, in pixels per second:
     * dead downwind, across the wind, and in the eye of it.
     *
     * Bare-poled and hull-1 because those are the only numbers this panel can honestly quote —
     * a rig and a hull come out of `config/boat.json`, which is a different file and a different
     * panel. What these say is what the **General** dials alone decide, which is the shape of the
     * sail curve rather than any particular ship's place on it. See design/Wind.md.
     *
     * They are the readout `windSpeed` and `windNoGoAngle` are actually judged by: those two
     * sliders move a curve, and a curve is not something anybody has an intuition for, but
     * "a hull in irons makes 50 and takes two minutes to cross" is.
     */
    speedRunning: number;
    speedBeamReach: number;
    speedIrons: number;
    /** The same three as crossing times, in seconds — the form the sea's size is felt in. */
    crossingRunning: number;
    crossingBeamReach: number;
    crossingIrons: number;
    /**
     * How much faster running is than lying in irons. **The single number the whole sailing
     * system is judged by**: at one the wind decides nothing and the arena is what it was before
     * any of this, and the further above one it goes the more a captain is rewarded for reading
     * the vane. `Infinity` if a hull in irons is stopped, which the floor under `BARE_SPEED`
     * exists to prevent.
     */
    sailSpread: number;
    /** Speed with the turbo lit, in pixels per second. */
    turboSpeed: number;
    /** Turbo speed as a multiple of cruise — the number design/Battle.md quotes. */
    turboMultiplier: number;
    /**
     * What a hull under turbo keeps of its cruise turn rate, as a multiple. One when
     * `turnSpeedFactor` is zero: speed used to cost nothing at the rudder.
     */
    turboTurnScale: number;
    /**
     * How much wider a hull's turning circle is under turbo than at cruise. Radius is speed
     * over turn rate, so this is `turboMultiplier / turboTurnScale` — the two halves of the
     * bargain multiplied together, and the number to judge the factor by.
     */
    turboTurnRadius: number;
    /**
     * How much a freshly joined hull has to be plundered of before the next ball sinks it, in
     * points. Zero when a spawn is already under the death line, which means one shot kills it.
     */
    mortalAfterLosing: number;
    /**
     * How long a freshly joined hull can hold the turbo before it hits the floor and cuts
     * out, in seconds — the gap between the two score dials, priced. Zero when a new boat
     * starts at or below the floor and can never burn at all.
     */
    newHullTurboSeconds: number;
    /** Half the arena. The viewport has to stay narrower than this or the seam resolves wrong. */
    halfArena: number;
    /**
     * How long the wind takes to swing right round to the opposite quarter, in seconds — the
     * legible form of a veer rate nobody has an intuition for. `Infinity` when the rate is zero,
     * which reads correctly: the wind never gets there.
     */
    windHalfTurnSeconds: number;
    /** The wind's strength as a multiple of a hull's cruise speed. What a sail will be weighed against. */
    windSpeedRatio: number;
    /**
     * What a minute at rank 1, rank 5 and rank 20 pays, in doubloons.
     *
     * **A rate per second is not a thing anyone can price**, and these three are. They are the
     * readout the purse dials are actually judged by, in the same way the crossing time is what
     * `mapSize` is judged by: nobody is choosing six doubloons a second, they are choosing whether
     * a good run buys a boat.
     */
    purseMinuteTop: number;
    purseMinuteFifth: number;
    purseMinuteTwentieth: number;
    /**
     * How many minutes at rank 10 buy the dearest boat in the catalogue.
     *
     * **The one readout in the dashboard that prices two panels against each other**, and it earns
     * the exception: a shop whose second boat is four hours away is a shop nobody sees, and nothing
     * on either panel alone would say so. It reads the *saved* `config/boat.json` rather than the
     * Boat panel's draft, because the two panels do not share state and an economy only has to be
     * roughly priced to be judged. `Infinity` when nothing costs anything.
     */
    purseMinutesForDearestBoat: number;
}

export function deriveGeneral(settings: GeneralSettings, dearestBoat = 0): GeneralDerived {
    // The sail curve, evaluated here rather than imported from shared/Wind.ts — which reads the
    // *saved* config and would therefore answer about the file rather than about the sliders a
    // designer is dragging. Same arithmetic, same constants, and the same reason `deriveGeneral`
    // already re-runs `turnScaleAtSpeed` instead of calling it.
    const bare = Math.max(settings.boatBaseSpeed * BARE_SHARE, settings.boatBaseSpeed - settings.windSpeed);
    const limitCos = Math.cos(Math.PI - (settings.windNoGoAngle * Math.PI) / 180);
    const power = (theta: number) => Math.max(0, (Math.cos(theta) - limitCos) / (1 - limitCos));

    const speedRunning = bare + settings.windSpeed * power(0);
    const speedBeamReach = bare + settings.windSpeed * power(Math.PI / 2);
    const speedIrons = bare + settings.windSpeed * power(Math.PI);
    // The same curve `turnScaleAtSpeed` in shared/Sailing.ts runs, evaluated at the one speed
    // ratio the dashboard can name without a hull in hand: flat out.
    const turboRatio = (settings.boatBaseSpeed + settings.turboSpeedIncrement) / settings.boatBaseSpeed;
    const turboTurnScale = 1 / (1 + settings.turnSpeedFactor * (turboRatio - 1));

    return {
        crossingSeconds: settings.mapSize / settings.boatBaseSpeed,
        speedRunning,
        speedBeamReach,
        speedIrons,
        crossingRunning: settings.mapSize / speedRunning,
        crossingBeamReach: settings.mapSize / speedBeamReach,
        crossingIrons: settings.mapSize / speedIrons,
        sailSpread: speedIrons > 0 ? speedRunning / speedIrons : Infinity,
        turboSpeed: settings.boatBaseSpeed + settings.turboSpeedIncrement,
        turboMultiplier: turboRatio,
        turboTurnScale: turboTurnScale,
        turboTurnRadius: turboRatio / turboTurnScale,
        newHullTurboSeconds: Math.max(0, settings.startingScore - settings.minimumBoatScore) / settings.turboCost,
        mortalAfterLosing: Math.max(0, settings.startingScore - settings.deathScore),
        halfArena: settings.mapSize / 2,
        windHalfTurnSeconds: 180 / settings.windVeerRate,
        windSpeedRatio: settings.windSpeed / settings.boatBaseSpeed,
        purseMinuteTop: purseRate(settings, 1) * 60,
        purseMinuteFifth: purseRate(settings, 5) * 60,
        purseMinuteTwentieth: purseRate(settings, 20) * 60,
        purseMinutesForDearestBoat: dearestBoat > 0 ? dearestBoat / (purseRate(settings, 10) * 60) : 0
    };
}

/**
 * Doubloons a second for holding `rank`, counting from 1. The rule from design/Shop.md, and the
 * **only** place it is evaluated — the arena pays on it and the dashboard prices it, and two
 * copies would be a panel that lies about the game.
 *
 * It lives here rather than in `shared/Battle.ts` because it is a pure function of a settings
 * object: `deriveGeneral` has to answer for the sliders a designer is *dragging*, not for the file
 * that is saved, so it cannot go through the loaded constants the way `plunderFrom` does.
 */
export function purseRate(settings: GeneralSettings, rank: number): number {
    const place = Math.max(1, Math.floor(rank));
    return settings.purseFloorRate + settings.purseTopRate / Math.pow(place, settings.purseFalloff);
}

/**
 * The `Loot` panel: the coins in the water — how many, what one is worth, where they sit, and
 * how far a hull reaches for one.
 *
 * These were `GeneralSettings` keys under a "Loot" heading until the panel existed. Unlike the
 * swell, which left `config/general.json` because the arena does not read it, this move is
 * **organisational**: loot is as sync-critical as anything in the game — the arena mints the
 * stock, spawns it and hands it out — and `config/loot.json` is read by both ends exactly as
 * `config/general.json` is. What changed is that a designer tuning the economy no longer has to
 * scroll past the map size and the wind to reach it.
 *
 * The keys **kept their `loot` prefix** rather than being shortened to `count` and `value` the
 * way the Sea life panel's are. They are quoted by name in design/Battle.md and in the sailing
 * records, and a rename would have made every one of those sentences wrong to buy nothing.
 */
export interface LootSettings {
    /** How many loot items float in the sea at once. Held constant: one taken, one respawned. */
    lootCount: number;
    /** Points one loot item is worth. */
    lootValue: number;
    /**
     * How many patches the sea's loot is gathered into. **Zero means uniform** — scattered
     * evenly, which is what the arena did before this was a dial. Above zero, the room mints
     * that many centres when it is created and spawns every item near one of them.
     */
    lootClusters: number;
    /** How tight one patch is, in world pixels. Only read when `lootClusters` is above zero. */
    lootClusterRadius: number;
    /**
     * How far a hull's magnet reaches for floating loot, in world pixels, measured from the
     * hull's centre. A coin inside it is dragged in rather than waited for. **Zero switches
     * the magnet off**, which is the arena as it was before the dial existed: a coin is taken
     * only by sailing over it.
     */
    lootMagnetRadius: number;
}

/** The values the game ships with. Kept equal to `config/loot.json` — see `GENERAL_DEFAULTS`. */
export const LOOT_DEFAULTS: LootSettings = {
    lootCount: 120,
    lootValue: 30,
    lootClusters: 0,
    lootClusterRadius: 600,
    lootMagnetRadius: 570
};

export const LOOT_FIELDS: SettingField<keyof LootSettings>[] = [
    {
        key: "lootCount",
        group: "The stock",
        label: "Loot in the sea",
        unit: "items",
        min: 0,
        max: 1000,
        step: 5,
        description:
            "How many coins float at once. Held exactly constant — one is respawned the moment one is taken — so this is the density of the whole sea, and a region cannot be stripped bare. Zero leaves the water empty apart from what ships spill."
    },
    {
        key: "lootValue",
        group: "The stock",
        label: "Loot value",
        unit: "pts",
        min: 1,
        max: 500,
        step: 1,
        description:
            "Points one coin is worth. It is also the size of the chunk the turbo is charged in, so a coin is exactly what a burning hull spills astern — raise it and both the reward and the trail get chunkier."
    },
    {
        key: "lootClusters",
        group: "Where it sits",
        label: "Loot patches",
        unit: "patches",
        min: 0,
        max: 40,
        step: 1,
        description:
            "How many patches the loot is gathered into. Zero scatters it evenly over the whole sea, which is even-handed but featureless; a handful of patches makes the map have places, worth sailing to and worth contesting. Centres are picked once per arena."
    },
    {
        key: "lootClusterRadius",
        group: "Where it sits",
        label: "Patch radius",
        unit: "px",
        min: 100,
        max: 5000,
        step: 50,
        description:
            "How tight one patch is. Ignored when there are no patches. Small enough and a patch is a single haul worth fighting over; wide enough and the patches overlap back into an even scatter."
    },
    {
        key: "lootMagnetRadius",
        group: "Taking it",
        label: "Magnet radius",
        unit: "px",
        min: 0,
        max: 3000,
        step: 10,
        description:
            "How far a hull reaches for a coin, from its centre. Anything inside it is dragged in, faster the closer it gets, so a near miss becomes a pickup and collecting reads as a boat that sucks the sea clean rather than one that has to thread a needle. The default is three hull lengths. Zero switches it off and coins have to be sailed over."
    }
];

const LOOT_BY_KEY = indexFields(LOOT_FIELDS);

/** Anything at all, as a usable `LootSettings`. See `coercePanel`. */
export function coerceLoot(raw: unknown): LootSettings {
    return coercePanel(LOOT_FIELDS, LOOT_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function lootField(key: keyof LootSettings): SettingField<keyof LootSettings> {
    return LOOT_BY_KEY.get(key)!;
}

/** Loot numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface LootDerived {
    /**
     * Average distance between two coins if they were spread evenly, in world pixels — the
     * legible form of "how much loot is in the sea", because it does not change meaning when
     * the map size does. `Infinity` when the sea is empty.
     */
    lootSpacing: number;
    /** Seconds of cruising between coins, at that spacing. What the density actually feels like. */
    lootSecondsApart: number;
    /** Every floating point in the sea added up, against which a hull's score can be read. */
    lootTotalValue: number;
    /** Seconds of turbo one coin buys. `Infinity` when the turbo is free. */
    turboSecondsPerCoin: number;
    /** How many coins a single patch holds, on average. Zero patches is the whole sea in one. */
    coinsPerPatch: number;
    /** The magnet's reach as a multiple of the gap between coins — over 1 is a permanent drag. */
    magnetVsSpacing: number;
}

/**
 * The loot, priced against the sea it floats in and the ship that collects it.
 *
 * The three numbers it needs from elsewhere are passed in rather than read here, for the same
 * reason `deriveWake` takes a cruise speed: this file holds no values from disk, and which sea
 * the dashboard quotes is a choice the panel makes.
 */
export function deriveLoot(settings: LootSettings, mapSize: number, cruiseSpeed: number, turboCost: number): LootDerived {
    // Even spread over a square sea: one coin per (mapSize^2 / count) of water, so the side
    // of that square is the gap. Infinity at a count of zero, which reads correctly.
    const spacing = mapSize / Math.sqrt(settings.lootCount);

    return {
        lootSpacing: spacing,
        lootSecondsApart: spacing / cruiseSpeed,
        lootTotalValue: settings.lootCount * settings.lootValue,
        turboSecondsPerCoin: settings.lootValue / turboCost,
        coinsPerPatch: settings.lootClusters > 0 ? settings.lootCount / settings.lootClusters : settings.lootCount,
        magnetVsSpacing: settings.lootMagnetRadius / spacing
    };
}

/**
 * The `Ocean` panel: the water itself — the swell the sea is drawn from, and the texture that
 * scrolls over it.
 *
 * The first four keys used to sit on `GeneralSettings` under a "The swell" heading. They moved
 * for the reason the school and the wake are separate files: **the sea is a picture on one
 * client**, not a field two machines have to agree about (design/Water.md),
 * so it belongs beside the other client-only dial sets rather than in the one file the arena
 * reads. Two clients with different water is two people looking at different pictures of the
 * same fight, which is the bargain [SeaLife.md](../design/SeaLife.md) already makes.
 *
 * The wind stays on the **General** panel, because a hull is sailed by it and the arena
 * publishes it. The sea only follows it around.
 */
export interface OceanSettings {
    /**
     * How far apart the swell's crests are, in world pixels. Read straight, with no rounding:
     * the sea is a picture on one client rather than a field two machines have to agree about,
     * so nothing snaps it to anything. See `src/scripts/sea/Waves.ts`.
     */
    waveLength: number;
    /** How fast a crest travels, in world pixels per second. */
    waveSpeed: number;
    /** How tall the sea is drawn, from a flat calm at 0 to as wild as the renderer goes at 1. */
    waveHeight: number;
    /**
     * How long the sea takes to swing round to a new wind, in seconds — the time constant of an
     * ease rather than a delay before a jump.
     *
     * It is a **look and nothing more**: waves push no hull (design/Water.md),
     * so what this decides is whether the water reads as weather or as water. Snap it to the wind
     * and the sea turns like a weathervane; leave it too long and it stops looking like the wind
     * has anything to do with it.
     */
    waveTurnSeconds: number;
    /**
     * Whether the scrolling surface texture is drawn at all — the `wave_overlay` frame of the
     * `DemoWater` sheet, layer 3 in design/Water.md. **0 or 1**, and a toggle in the dashboard.
     *
     * Switching it off is not the same as a calm. The swell's *warp* has nothing left to bend,
     * because the layer under it is flat colour, so the surface detail goes entirely; what stays
     * is the filter's own shading, its whitecaps and its depth ramp, which are computed from the
     * wave field rather than sampled from the plate. That is a legible look — a flat, banded,
     * poster sea — and it is also the fastest way to see what the overlay is actually doing.
     */
    overlay: number;
    /**
     * How fast that texture drifts across the water, in **tile-space** pixels per second. On
     * screen it is halved by the tile scale, which the panel's readout does for you.
     *
     * It is drift *on top of* the sea's own travel: the overlay is anchored to the world and
     * carried by the camera like everything else, and this is the extra crawl that stops still
     * water reading as a photograph. Zero is a texture pinned to the sea, which is not wrong —
     * the swell still moves through it — just very calm.
     */
    overlaySpeed: number;
    /**
     * The flat colour the whole sea is built on — layer 1 of the plate in design/Water.md, and
     * the colour everything drawn *in* the water is washed toward so that it reads as submerged.
     *
     * It is the base every other colour here is measured from, and one thing about it is worth
     * knowing before it is moved: the depth ramp is **multiplicative**, so a channel this colour
     * has none of cannot be put back by a trough or a crest. The shipped `#0090f4` has no red in
     * it at all.
     */
    seaColor: string;
    /**
     * What the water is tinted toward in the bottom of a **trough**, and toward on a **crest**.
     *
     * These are the sea's contrast, and the pair a designer actually judges: green-deep below and
     * pale above is what reads as depth, where a dark blue and a light blue read as the same
     * water in shadow. Stored as the colours the water lands on, converted to the multipliers the
     * shader wants by `tintBetween` — see `OceanFilter.ts` for why it multiplies rather than
     * mixing a flat colour in.
     *
     * They are the tint and not the final pixel: the banded light lifts and drops the water
     * either side of this, and `waveHeight` fades both toward no tint at all as it comes down.
     */
    troughColor: string;
    crestColor: string;
    /**
     * The one white on the water: the wake's foam and the whitecaps on a breaking crest are the
     * same colour out of the same cut in the same channel.
     *
     * A very pale blue rather than pure white, shipped — on `#0090f4` a true white reads as a
     * hole in the sea rather than as churn in it.
     */
    foamColor: string;
}

/** The keys of the four colours, narrowed — a `ColorField` may only ever name a string one. */
export type OceanColorKey = "seaColor" | "troughColor" | "crestColor" | "foamColor";

/** Everything else on the panel: the numbers, which are what `SettingField` describes. */
export type OceanDialKey = Exclude<keyof OceanSettings, OceanColorKey>;

/** The values the game ships with. Kept equal to `config/ocean.json` — see `GENERAL_DEFAULTS`. */
export const OCEAN_DEFAULTS: OceanSettings = {
    waveLength: 1000,
    waveSpeed: 200,
    waveHeight: 0.6,
    waveTurnSeconds: 20,
    overlay: 1,
    overlaySpeed: 30,
    seaColor: "#0090f4",
    troughColor: "#00738e",
    crestColor: "#009cf9",
    foamColor: "#eaf6ff"
};

export const OCEAN_FIELDS: SettingField<OceanDialKey>[] = [
    {
        key: "waveLength",
        group: "The swell",
        label: "Wave length",
        unit: "px",
        min: 100,
        max: 2000,
        step: 10,
        description:
            "How far apart the crests are. Read it against a hull: at the shipped 1000 a sloop is a fifth of a wave long, so a boat sits on one rather than spanning several. Taken literally — the sea is drawn by one client rather than agreed between two, so nothing rounds this to make it tile."
    },
    {
        key: "waveSpeed",
        group: "The swell",
        label: "Wave speed",
        unit: "px/s",
        min: 0,
        max: 400,
        step: 5,
        description:
            "How fast a crest travels. It is a look rather than a mechanic — no hull is pushed by it — so judge it by eye: too slow and the sea reads as corrugated iron sliding past, too fast and it reads as rain on a window. Zero is a standing swell, which is not a sea but is a useful thing to look at."
    },
    {
        key: "waveHeight",
        group: "The swell",
        label: "Wave height",
        unit: "\u00d7",
        min: 0,
        max: 1,
        step: 0.05,
        description:
            "How tall the sea is drawn, from a flat calm to as wild as the renderer goes. It is the dial most likely to be turned up too far: ships, loot and damage numbers all have to stay readable on top of the water, and the sea wins that fight long before it stops looking good."
    },
    {
        key: "waveTurnSeconds",
        group: "The swell",
        label: "Wave turn",
        unit: "s",
        min: 1,
        max: 120,
        step: 1,
        description:
            "How long the sea takes to swing round to a new wind — a time constant, so it covers the same fraction of whatever is left each second rather than waiting and then jumping. Purely a look: nothing is sailed by the waves, so what this decides is whether the water reads as weather or as water. Short and the sea turns like a weathervane, which reads as a wind effect rather than a sea; long and it stops looking like the wind has anything to do with it. Real swell lags its weather badly, so err long."
    },
    {
        key: "overlay",
        group: "The surface",
        label: "Surface texture",
        unit: "",
        min: 0,
        max: 1,
        step: 1,
        kind: "toggle",
        description:
            "Whether the scrolling wave_overlay layer is drawn over the water at all. It is the layer the swell's warp actually bends, so switching it off leaves a flat sea with the filter's shading, whitecaps and depth ramp still on it — a poster sea rather than a calm one. The quickest way to see what this layer is worth is to turn it off and back on."
    },
    {
        key: "overlaySpeed",
        group: "The surface",
        label: "Surface drift",
        unit: "px/s",
        min: 0,
        max: 200,
        step: 1,
        description:
            "How fast that texture crawls across the sea, in tile-space pixels per second — the readout on the right gives it in screen pixels. This is drift on top of the water's own travel, so it is what stops a ship sitting still from looking like a photograph. Turn it well up and the sea starts to read as a river."
    }
];

/**
 * The sea's palette. A second array beside `OCEAN_FIELDS` rather than four more rows in it,
 * because a colour is a different kind of field — see `ColorField`.
 */
export const OCEAN_COLORS: ColorField<OceanColorKey>[] = [
    {
        key: "seaColor",
        group: "Colours",
        label: "Base water",
        description:
            "The flat colour the whole sea is built on, and the colour anything drawn under the water is washed toward. Every other colour here is a tint on this one, so moving it moves the trough and the crest with it — and because a tint multiplies, a channel this colour has none of can never be put back. The shipped blue has no red in it at all."
    },
    {
        key: "troughColor",
        group: "Colours",
        label: "Trough",
        description:
            "What the water reads as at the bottom of a wave. Green-deep against a pale crest is what gives the sea depth: two colours that differ in hue read as different distances, where two that differ only in brightness read as the same water in shadow. Wave height fades this toward no tint at all as it comes down."
    },
    {
        key: "crestColor",
        group: "Colours",
        label: "Crest",
        description:
            "What the water reads as at the top of a wave, before the banded light and any whitecap on it. Keep the gap to the trough wide enough to see and narrow enough that the sea does not start competing with the ships on top of it."
    },
    {
        key: "foamColor",
        group: "Colours",
        label: "Foam",
        description:
            "The one white on the water: a hull's wake and a breaking crest are the same colour out of the same cut. Pure white reads as a hole in the sea rather than as churn in it, so the shipped colour is a very pale blue."
    }
];

const OCEAN_BY_KEY = indexFields(OCEAN_FIELDS);

/** Anything at all, as a usable `OceanSettings`. See `coercePanel` and `coerceColors`. */
export function coerceOcean(raw: unknown): OceanSettings {
    return coerceColors(OCEAN_COLORS, coercePanel(OCEAN_FIELDS, OCEAN_DEFAULTS, raw), raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function oceanField(key: OceanDialKey): SettingField<OceanDialKey> {
    return OCEAN_BY_KEY.get(key)!;
}

/**
 * The overlay's tile scale — `WAVES_TILE_SCALE` in
 * [../src/scripts/sea/Water.ts](../src/scripts/sea/Water.ts). Copied here for the same reason
 * `SWELL_DISPLACEMENT` is: this file may hold no values from disk and may import nothing, and a
 * drift speed in tile space means nothing to anybody until it is quoted on screen.
 */
const OVERLAY_TILE_SCALE = 0.5;

/** Ocean numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface OceanDerived {
    /** Seconds between two crests passing a fixed point. `Infinity` for a standing swell. */
    crestPeriod: number;
    /** How many hull lengths there are between crests — the ruler a wavelength is judged by. */
    waveInHulls: number;
    /** How many crests are on a reference-width screen at once. */
    wavesOnScreen: number;
    /** The overlay's drift in **screen** pixels per second, which is what the eye reads. */
    overlayDrift: number;
    /** Whether the sea is dialled flat: no swell effects at all, however the overlay is set. */
    flat: boolean;
    /**
     * The channels the base colour has none of, named. Nothing a trough or a crest asks for in
     * one of these can show, because the depth ramp multiplies — see `tintBetween`.
     */
    deadChannels: string[];
    /**
     * How far apart the trough and the crest are, 0 to 1: the largest gap between them on any
     * channel, as a fraction of full scale. The sea's contrast, as one number.
     */
    contrast: number;
}

export function deriveOcean(settings: OceanSettings): OceanDerived {
    const base = rgbOf(settings.seaColor);
    const trough = rgbOf(settings.troughColor);
    const crest = rgbOf(settings.crestColor);

    const dead: string[] = [];
    if (base.r === 0) dead.push("red");
    if (base.g === 0) dead.push("green");
    if (base.b === 0) dead.push("blue");

    return {
        deadChannels: dead,
        contrast: Math.max(Math.abs(crest.r - trough.r), Math.abs(crest.g - trough.g), Math.abs(crest.b - trough.b)) / 255,
        crestPeriod: settings.waveSpeed > 0 ? settings.waveLength / settings.waveSpeed : Infinity,
        waveInHulls: settings.waveLength / REFERENCE_HULL,
        wavesOnScreen: REFERENCE_VIEWPORT / settings.waveLength,
        overlayDrift: settings.overlay === 0 ? 0 : settings.overlaySpeed * OVERLAY_TILE_SCALE,
        flat: settings.waveHeight === 0
    };
}

/**
 * The `Sea life` panel: the fish, sharks, rays and turtles that drift under the water.
 *
 * Pure decoration. Nothing here reaches the server, nothing here is networked, and no boat
 * can touch any of it — see design/SeaLife.md. That is why it can be a client-only dial set
 * where `GeneralSettings` cannot: two clients disagreeing about how many turtles there are
 * is not a desync, it is two people looking at different water.
 *
 * **Almost everything here belongs to one animal.** Only the opacity and the depth wash are the
 * school's; how many there are, how big they are drawn, how fast they swim, how lazily they come
 * about and how quickly they flap are all set per species, as `<species><Dial>` — `sharkSizeMax`,
 * `fishCount`, `rayAnimationFps`. The keys are generated from the two lists below rather than
 * written out thirty-five times, so adding a sixth animal is one entry in each.
 *
 * They were shared dials once, with a length factor and a cadence factor per species in
 * `SeaLife.ts` scaling a single range. Those factors were a workaround for having one slider:
 * they are gone, and their effect is baked into these defaults. See design/SeaLife.md.
 */

/** The five animals in the sheet, in the order the dashboard draws them. */
export const SEALIFE_SPECIES = ["fish", "hammer", "ray", "shark", "turtle"] as const;

export type SeaLifeSpecies = (typeof SEALIFE_SPECIES)[number];

/**
 * What each animal is called, singular for a heading and plural for a sentence.
 *
 * The client's roster in [../src/scripts/sea/SeaLife.ts](../src/scripts/sea/SeaLife.ts) is keyed
 * by the same names and carries the other half of a species — which animation it plays. A rename
 * here is a build error there rather than an animal that silently stops appearing.
 */
export const SEALIFE_SPECIES_NAMES: Record<SeaLifeSpecies, { one: string; many: string }> = {
    fish: { one: "Fish", many: "fish" },
    hammer: { one: "Hammerhead", many: "hammerheads" },
    ray: { one: "Manta ray", many: "manta rays" },
    shark: { one: "Shark", many: "sharks" },
    turtle: { one: "Sea turtle", many: "sea turtles" }
};

/** The seven dials every species carries, in the order the popup lays them out. */
export const SEALIFE_SPECIES_DIALS = ["count", "sizeMin", "sizeMax", "speedMin", "speedMax", "turnRate", "animationFps"] as const;

export type SeaLifeSpeciesDial = (typeof SEALIFE_SPECIES_DIALS)[number];

/** A stored species key: `"shark"` and `"sizeMax"` make `"sharkSizeMax"`. */
export type SeaLifeSpeciesKey = `${SeaLifeSpecies}${Capitalize<SeaLifeSpeciesDial>}`;

/** The key one species' dial is stored under. The one place that spelling is decided. */
export function speciesDial(species: SeaLifeSpecies, dial: SeaLifeSpeciesDial): SeaLifeSpeciesKey {
    return `${species}${dial[0].toUpperCase()}${dial.slice(1)}` as SeaLifeSpeciesKey;
}

export interface SeaLifeSettings extends Record<SeaLifeSpeciesKey, number> {
    /** How solid an animal is drawn, 0 to 1. Well under 1: they are under the surface. */
    opacity: number;
    /**
     * How far each animal's colour is washed towards the sea's own, 0 to 1. This is the dial
     * that says "how deep" — at 1 an animal is a shape in the water rather than a thing in it.
     */
    depth: number;
}

/** One species' dials, unflattened. What `seaLifeSpecies` reads out and the popup edits. */
export type SpeciesDials = Record<SeaLifeSpeciesDial, number>;

/** One species' dials, out of the flat stored object. */
export function seaLifeSpecies(settings: SeaLifeSettings, species: SeaLifeSpecies): SpeciesDials {
    const out = {} as SpeciesDials;
    for (const dial of SEALIFE_SPECIES_DIALS) out[dial] = settings[speciesDial(species, dial)];
    return out;
}

/**
 * How many animals are under the viewport altogether.
 *
 * Not stored, deliberately: a total alongside five populations is a second place for the same
 * number to be wrong, and the panel edits the species. See design/SeaLife.md.
 */
export function seaLifePopulation(settings: SeaLifeSettings): number {
    let total = 0;
    for (const species of SEALIFE_SPECIES) total += settings[speciesDial(species, "count")];
    return total;
}

/**
 * What one species dial is called and what it may be set to. The same for every animal — a shark
 * and a fish differ in what their dials are set *to*, which is the defaults below, not in what
 * they are allowed to be.
 */
const SEALIFE_DIAL_META: Record<SeaLifeSpeciesDial, Omit<SettingField<string>, "key" | "group" | "description"> & { about: (many: string) => string }> = {
    count: {
        label: "In the water",
        unit: "animals",
        min: 0,
        max: 20,
        step: 1,
        about: (many) =>
            `How many ${many} are under the viewport at once. Held constant — one is respawned off screen the moment one swims out — so this is a density rather than a budget, and the water is never briefly empty.`
    },
    sizeMin: {
        label: "Smallest",
        unit: "px",
        min: 10,
        max: 600,
        step: 1,
        about: (many) => `On-screen length of the smallest of the ${many}. A hull is 190px bow to stern, which is what everything in the arena is judged against.`
    },
    sizeMax: {
        label: "Largest",
        unit: "px",
        min: 10,
        max: 600,
        step: 1,
        about: (many) => `On-screen length of the largest of the ${many}. Push it past a hull length and one going by is longer than the ship sailing over it — a feeling worth having, not a bug.`
    },
    speedMin: {
        label: "Slowest",
        unit: "px/s",
        min: 1,
        max: 400,
        step: 1,
        about: (many) =>
            `Speed of the slowest of the ${many}. Judge it against the boat base speed on the General panel — sea life moving anywhere near a hull stops reading as scenery and starts reading as traffic.`
    },
    speedMax: {
        label: "Fastest",
        unit: "px/s",
        min: 1,
        max: 400,
        step: 1,
        about: (many) =>
            `Speed of the fastest of the ${many}. Size and speed come from the same roll, deliberately inverted, so the fastest is also the smallest and a big silhouette is always a slow one.`
    },
    turnRate: {
        label: "Turn rate",
        unit: "deg/s",
        min: 0,
        max: 180,
        step: 1,
        about: (many) =>
            `How fast the ${many} come about while wandering. Low is the point: sea life that jinks reads as something being chased, and nothing down there is being chased. Zero sends them across in a straight line.`
    },
    animationFps: {
        label: "Swim cadence",
        unit: "fps",
        min: 1,
        max: 60,
        step: 1,
        about: (many) =>
            `Rate of the swim cycle for the ${many} — a ray flaps and a fish wriggles, and this is where that difference lives now. Independent of the rate the game is drawing at, so it means the same on any machine.`
    }
};

/**
 * A school that is noticed rather than watched. Eight animals under a viewport is roughly one
 * in shot at any moment, which is the point — sea life is meant to be caught out of the
 * corner of the eye while the player is busy with something that can shoot back.
 *
 * Two each of the three commoner animals and one each of the two that carry the most weight on
 * screen: a shark is the longest thing down there and reads best when one going past is an event
 * rather than the usual traffic.
 *
 * The sizes and cadences are the old shared range through the old per-species factors — a shark
 * at 1.2× of 60–190px is 72–228px, a ray at 0.5× of 24fps flaps at 12 — so the water looks the
 * same as it did before these became dials. The speeds never had a factor and are the same range
 * for everything until somebody decides otherwise, which is now a thing they can decide.
 */
const SEALIFE_SPECIES_DEFAULTS: Record<SeaLifeSpecies, SpeciesDials> = {
    fish: { count: 2, sizeMin: 21, sizeMax: 67, speedMin: 82, speedMax: 120, turnRate: 26, animationFps: 31 },
    hammer: { count: 2, sizeMin: 69, sizeMax: 219, speedMin: 82, speedMax: 120, turnRate: 26, animationFps: 20 },
    ray: { count: 2, sizeMin: 60, sizeMax: 190, speedMin: 82, speedMax: 120, turnRate: 26, animationFps: 12 },
    shark: { count: 1, sizeMin: 72, sizeMax: 228, speedMin: 82, speedMax: 120, turnRate: 26, animationFps: 19 },
    turtle: { count: 1, sizeMin: 48, sizeMax: 152, speedMin: 82, speedMax: 120, turnRate: 26, animationFps: 14 }
};

/** The two dials the whole school shares. Drawn as ordinary rows, unlike the thirty-five below. */
export const SEALIFE_SCHOOL_FIELDS: SettingField<keyof SeaLifeSettings>[] = [
    {
        key: "opacity",
        group: "The school",
        label: "Opacity",
        unit: "",
        min: 0,
        max: 1,
        step: 0.05,
        description:
            "How solid an animal is drawn. Full strength puts them on the surface, which is the one thing they must not read as; low enough and they are a suggestion of movement rather than an animal."
    },
    {
        key: "depth",
        group: "The school",
        label: "Depth wash",
        unit: "",
        min: 0,
        max: 1,
        step: 0.05,
        description:
            "How far an animal's colour is pulled towards the sea's own blue. This is the dial that says how deep it is swimming: at zero it is its own colour just under the surface, at one it is a darker patch of the same water."
    }
];

/**
 * Every field on the panel: the two school dials, then seven per animal.
 *
 * Generated rather than written out, which is what keeps thirty-five rows from being thirty-five
 * chances to paste the wrong range. The per-species runs are grouped under the animal's own name,
 * so a field list handed straight to `SettingRows` would come out as a card per animal — the panel
 * draws them as a popup instead, but the grouping is what says they belong together.
 */
export const SEALIFE_FIELDS: SettingField<keyof SeaLifeSettings>[] = [
    ...SEALIFE_SCHOOL_FIELDS,
    ...SEALIFE_SPECIES.flatMap((species) =>
        SEALIFE_SPECIES_DIALS.map((dial) => {
            const meta = SEALIFE_DIAL_META[dial];
            return {
                key: speciesDial(species, dial),
                group: SEALIFE_SPECIES_NAMES[species].one,
                label: meta.label,
                unit: meta.unit,
                min: meta.min,
                max: meta.max,
                step: meta.step,
                description: meta.about(SEALIFE_SPECIES_NAMES[species].many)
            };
        })
    )
];

export const SEALIFE_DEFAULTS: SeaLifeSettings = {
    opacity: 0.7,
    depth: 0.5,
    ...(() => {
        const out = {} as Record<SeaLifeSpeciesKey, number>;
        for (const species of SEALIFE_SPECIES) {
            for (const dial of SEALIFE_SPECIES_DIALS) out[speciesDial(species, dial)] = SEALIFE_SPECIES_DEFAULTS[species][dial];
        }
        return out;
    })()
};

const SEALIFE_BY_KEY = indexFields(SEALIFE_FIELDS);

/** Anything at all, as a usable `SeaLifeSettings`. See `coercePanel`. */
export function coerceSeaLife(raw: unknown): SeaLifeSettings {
    return coercePanel(SEALIFE_FIELDS, SEALIFE_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function seaLifeField(key: keyof SeaLifeSettings): SettingField<keyof SeaLifeSettings> {
    return SEALIFE_BY_KEY.get(key)!;
}

/** One species' place in the school, and the numbers that fall out of its own dials. */
export interface SpeciesReadout {
    key: SeaLifeSpecies;
    label: string;
    /** How many of this animal are in the water. Exactly what the panel stores. */
    count: number;
    /** Fraction of the school, 0 to 1. Zero for every species when the school is empty. */
    share: number;
    /** Average speed of this animal, in pixels per second. */
    averageSpeed: number;
    /** Average on-screen length of this animal, in pixels. */
    averageSize: number;
    /** How long one takes to cross a reference viewport at that speed, in seconds. */
    crossingSeconds: number;
    /** How many of this animal swim into shot per minute. */
    arrivalsPerMinute: number;
}

/** Sea-life numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface SeaLifeDerived {
    /** Every species added up: how many animals are under the viewport at once. */
    population: number;
    /** Average speed across the school, in pixels per second, weighted by population. */
    averageSpeed: number;
    /** Average on-screen length across the school, in pixels, weighted by population. */
    averageSize: number;
    /** How long the average animal takes to cross a reference viewport, in seconds. */
    crossingSeconds: number;
    /** How many animals swim into shot per minute, summed over the species. */
    arrivalsPerMinute: number;
    /** Each species: how many there are, what share of the school, and its own averages. */
    mix: SpeciesReadout[];
}

/**
 * The viewport the readouts are quoted against, in pixels. Sea life is placed in screen space,
 * so "how often does one go past" depends on how wide the window is; 1920 is quoted because it
 * is the window most people will be judging the dials on.
 */
const REFERENCE_VIEWPORT = 1920;

export function deriveSeaLife(settings: SeaLifeSettings): SeaLifeDerived {
    const mix = SEALIFE_SPECIES.map((species) => {
        const dials = seaLifeSpecies(settings, species);

        // Sorted rather than trusted: fields are policed one at a time, so a min above its max is
        // a storable state, and every reader has to take the pair as a range either way round.
        const averageSpeed = (Math.min(dials.speedMin, dials.speedMax) + Math.max(dials.speedMin, dials.speedMax)) / 2;
        const crossingSeconds = REFERENCE_VIEWPORT / averageSpeed;

        return {
            key: species,
            label: SEALIFE_SPECIES_NAMES[species].one,
            count: dials.count,
            share: 0,
            averageSpeed,
            averageSize: (Math.min(dials.sizeMin, dials.sizeMax) + Math.max(dials.sizeMin, dials.sizeMax)) / 2,
            crossingSeconds,
            arrivalsPerMinute: (dials.count / crossingSeconds) * 60
        };
    });

    const population = mix.reduce((sum, species) => sum + species.count, 0);
    // An empty school has no shares rather than five even ones: the counts are exact, and a mix
    // quoted off nothing would be inventing a school that is not there.
    for (const species of mix) species.share = population > 0 ? species.count / population : 0;

    // Weighted by population, so the school's average is the average animal in it rather than the
    // average row on the panel. An empty school falls back to the plain mean across the species,
    // which is the only reading left that says anything about the dials as they stand.
    const weighted = (pick: (species: SpeciesReadout) => number) =>
        population > 0
            ? mix.reduce((sum, species) => sum + pick(species) * species.count, 0) / population
            : mix.reduce((sum, species) => sum + pick(species), 0) / mix.length;

    const averageSpeed = weighted((species) => species.averageSpeed);

    return {
        population,
        averageSpeed,
        averageSize: weighted((species) => species.averageSize),
        crossingSeconds: REFERENCE_VIEWPORT / averageSpeed,
        arrivalsPerMinute: mix.reduce((sum, species) => sum + species.arrivalsPerMinute, 0),
        mix
    };
}

/**
 * The `Wake` panel: the trail a hull leaves **on the water** behind it.
 *
 * Not the same thing as the stern foam in [../src/scripts/boats/Boat.ts](../src/scripts/boats/Boat.ts),
 * which is welded to the ship and turns with it. This is the mark left in the sea at the place
 * the ship passed through: it is dropped once and then belongs to the water, drifting apart and
 * fading where it was put. See design/Water.md.
 *
 * Client-only dials, like the school and for the same reason — a trail is a picture of
 * something the arena has already decided, so two clients drawing different foam is two people
 * looking at different water rather than a desync.
 */
export interface WakeSettings {
    /**
     * How far a hull sails between one pair of foam marks and the next, in world pixels. This
     * is a **distance**, not an interval: a boat sitting still leaves nothing behind it, and a
     * boat under turbo leaves the same trail laid down faster.
     */
    spacing: number;
    /** How long one mark takes to disappear, in seconds. What sets how far astern a trail reaches. */
    life: number;
    /** How wide a fresh mark is drawn, in world pixels. */
    widthStart: number;
    /** How wide a mark has grown by the end of its life. Foam spreads as it dies. */
    widthEnd: number;
    /**
     * How solid the foam reads where there is any, 0 to 1. Not the strength of one mark: the
     * marks accumulate into a field and the filter cuts foam out of it at a threshold, so this
     * says how white that comes out, not how much of it there is.
     */
    opacity: number;
    /** How far behind the hull's centre the trail starts, as a fraction of that hull's length. */
    sternOffset: number;
    /**
     * How far off the centreline each arm of the V starts, as a fraction of the hull's beam.
     * The trail is laid as **two** lines rather than one, because that is the shape a hull
     * actually leaves — a single stripe reads as a smear.
     */
    armSpread: number;
    /** How fast the two arms drift apart once laid, in pixels per second. This opens the V. */
    spreadRate: number;
    /** How much wider a mark laid under turbo is, as a multiple of the normal width. */
    boostWidth: number;
    /** How much more solid a mark laid under turbo is, as a multiple of the normal opacity. */
    boostOpacity: number;
    /**
     * How hard the trail **bends the water under it**, in pixels of displacement at the steepest
     * point of a mark. This is the second thing a trail does and the one that is not paint: the
     * marks are rendered into a map and the whole sea is drawn through it, so the waves themselves
     * bow around a course a ship has taken. **Zero leaves the foam** and stops building the half
     * of each mark that steers the water.
     */
    displacement: number;
    /**
     * The most marks that may be on the water at once, across every hull in shot. A ceiling
     * rather than a target: the oldest mark is taken back when a new one is needed and there
     * are none spare, so a crowded sea loses the tail of the longest trail rather than the
     * frame rate.
     */
    budget: number;
}

/**
 * A trail that is noticed rather than watched, on the same principle as the school. Three
 * seconds at cruise is about 350px of foam — two hull lengths on the starting boat, so a ship
 * trails something a little longer than itself and the sea does not turn into a road map.
 */
export const WAKE_DEFAULTS: WakeSettings = {
    spacing: 12,
    life: 5,
    widthStart: 32,
    widthEnd: 92,
    opacity: 0.42,
    sternOffset: 0.45,
    armSpread: 0.26,
    spreadRate: 26,
    boostWidth: 1.35,
    boostOpacity: 1.5,
    displacement: 45,
    budget: 1500
};

export const WAKE_FIELDS: SettingField<keyof WakeSettings>[] = [
    {
        key: "life",
        group: "The track",
        label: "Trail life",
        unit: "s",
        min: 0,
        max: 12,
        step: 0.1,
        description:
            "How long a mark lasts before it has faded to nothing. Together with cruise speed this is how far astern a trail reaches — the readouts on the right price it in hull lengths. Zero switches the trail off."
    },
    {
        key: "spacing",
        group: "The track",
        label: "Mark spacing",
        unit: "px",
        min: 4,
        max: 80,
        step: 1,
        description:
            "How far the ship sails between one pair of marks and the next. Smaller is a smoother line and more marks on the water; past about the width of a mark the foam stops joining up and the trail beads."
    },
    {
        key: "budget",
        group: "The track",
        label: "Marks on the water",
        unit: "marks",
        min: 0,
        max: 3000,
        step: 50,
        description:
            "The ceiling across every hull in shot. When it is reached the oldest mark is taken back for the newest, so a busy sea shortens the longest trails instead of dropping frames. Zero switches the trail off."
    },
    {
        key: "widthStart",
        group: "The foam",
        label: "Fresh width",
        unit: "px",
        min: 2,
        max: 200,
        step: 1,
        description: "How wide a mark is the moment it is laid, in world pixels — measured against a hull, not against a screen."
    },
    {
        key: "widthEnd",
        group: "The foam",
        label: "Spent width",
        unit: "px",
        min: 2,
        max: 400,
        step: 1,
        description: "How wide the same mark has grown by the time it disappears. Foam disperses, so this wants to be the larger number."
    },
    {
        key: "opacity",
        group: "The foam",
        label: "Opacity",
        unit: "",
        min: 0,
        max: 1,
        step: 0.01,
        description:
            "How solid the foam reads wherever there is any. It is not how strong one mark is: the marks add up into a field and the filter then cuts foam out of that at a threshold, so this decides how white the result is drawn and not how much of it there is. Keep it low or the sea turns into a whiteboard."
    },
    {
        key: "sternOffset",
        group: "The V",
        label: "Stern offset",
        unit: "x hull length",
        min: 0,
        max: 1,
        step: 0.01,
        description:
            "How far behind the hull's centre the trail begins. In hull lengths rather than pixels, so a galleon's trail leaves a galleon's stern and not the middle of its deck."
    },
    {
        key: "armSpread",
        group: "The V",
        label: "Arm spread",
        unit: "x hull beam",
        min: 0,
        max: 2,
        step: 0.01,
        description:
            "How far off the centreline each of the two arms is laid, in hull beams. At zero the arms land on top of each other and the trail is a single stripe."
    },
    {
        key: "spreadRate",
        group: "The V",
        label: "Opening rate",
        unit: "px/s",
        min: 0,
        max: 120,
        step: 1,
        description:
            "How fast the two arms drift apart once they are on the water. This is what makes the trail a V rather than two parallel lines — it is the only motion a mark has of its own."
    },
    {
        key: "displacement",
        group: "The water",
        label: "Bend the water",
        unit: "px",
        min: 0,
        max: 120,
        step: 1,
        description:
            "How far the sea is pushed around under a mark, at the steepest point of it. This is the half of the trail that is not paint — the waves bow around a course rather than having foam laid over them. Zero leaves the foam and stops building the half of each mark that steers the water."
    },
    {
        key: "boostWidth",
        group: "Turbo",
        label: "Turbo width",
        unit: "x",
        min: 1,
        max: 4,
        step: 0.05,
        description: "How much wider a mark laid with the turbo lit is. The boost already lays marks faster; this makes it look harder too."
    },
    {
        key: "boostOpacity",
        group: "Turbo",
        label: "Turbo opacity",
        unit: "x",
        min: 1,
        max: 4,
        step: 0.05,
        description:
            "How much more solid a mark laid with the turbo lit is. Capped at full opacity on the way out, so a large multiplier saturates rather than breaking."
    }
];

const WAKE_BY_KEY = indexFields(WAKE_FIELDS);

/** Anything at all, as a usable `WakeSettings`. See `coercePanel`. */
export function coerceWake(raw: unknown): WakeSettings {
    return coercePanel(WAKE_FIELDS, WAKE_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function wakeField(key: keyof WakeSettings): SettingField<keyof WakeSettings> {
    return WAKE_BY_KEY.get(key)!;
}

/** Wake numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface WakeDerived {
    /** How far astern the trail reaches at cruise, in world pixels. */
    trailLength: number;
    /** The same distance in starting-hull lengths, which is the unit a player reads it in. */
    trailInHulls: number;
    /** How many marks one cruising hull keeps on the water. Two arms, so the pair count doubled. */
    marksPerBoat: number;
    /** How many hulls can trail at once before the budget starts taking the oldest marks back. */
    boatsCovered: number;
    /** How far apart the two arms have drifted by the tail of the trail, in pixels. */
    openingAtTail: number;
    /** Whether consecutive marks still overlap. False is a beaded trail rather than a line. */
    joinsUp: boolean;
    /** How the warp compares to the sea's own swell, which is the thing it has to read against. */
    displacementVsSwell: number;
}

/**
 * The hull the readouts are quoted against, in pixels. Level 1's length — the scale reference
 * the whole arena is measured in, per shared/Ships.ts — because "two hull lengths of foam" is
 * the thing a designer can actually picture, and a raw pixel count is not.
 */
const REFERENCE_HULL = 190;
/**
 * The sea's own displacement amplitude, in pixels — `DISPLACEMENT_SCALE` in
 * [../src/scripts/sea/Water.ts](../src/scripts/sea/Water.ts). Quoted here so the dashboard can
 * say what the trail's warp is *relative to*, which is the only way that number means anything.
 * It is a decision rather than a dial, so this is a copy of one and not a second source of it.
 */
const SWELL_DISPLACEMENT = 80;

export function deriveWake(settings: WakeSettings, cruiseSpeed: number): WakeDerived {
    const trailLength = cruiseSpeed * settings.life;
    // Two arms per emission, and one emission per `spacing` of water covered.
    const marksPerBoat = settings.spacing > 0 ? (trailLength / settings.spacing) * 2 : 0;

    return {
        trailLength,
        trailInHulls: trailLength / REFERENCE_HULL,
        marksPerBoat,
        boatsCovered: marksPerBoat > 0 ? settings.budget / marksPerBoat : Infinity,
        openingAtTail: settings.spreadRate * settings.life * 2,
        displacementVsSwell: settings.displacement / SWELL_DISPLACEMENT,
        // A mark is a soft round blob, so the halves of two neighbours have to reach each
        // other: they join while the gap between centres is inside one fresh width.
        joinsUp: settings.spacing <= settings.widthStart
    };
}

/**
 * The `Shot` panel: the cannonball in the air — how big it is, and the arc it appears to fly.
 *
 * There is **no height in the simulation**. A ball is a point travelling in a straight line at a
 * constant speed until it runs out of range or the arena says it hit something, on both ends,
 * and nothing here changes any of that: a shot connects at any point in its curve, exactly as it
 * would with the dials at zero. What these numbers buy is the *picture* of a lob — the ball
 * swells as it climbs away from the deck and shrinks back as it falls to the sea — which is the
 * only cue a top-down view has for height. See design/Settings.md.
 *
 * Client-only, like the school and the wake, and for the same reason: two clients drawing a ball
 * at different sizes is a difference of opinion about pictures, not a desync. The flight itself
 * is dead-reckoned from the arena's own five numbers and is identical on every screen.
 */
export interface ShotSettings {
    /**
     * How wide a ball is drawn at the muzzle, in **world** pixels — the camera zooms, so this is
     * measured against a hull rather than against the screen. The size the arc multiplies.
     */
    size: number;
    /**
     * How much is **added** to that size at the top of the arc, as a multiple of it: at 1 the
     * ball is twice its muzzle size when it is highest. **Zero flies flat**, which is the arena
     * as it was before the dial existed — the ball is one size from the muzzle to the water.
     */
    arcHeight: number;
    /**
     * Where along the flight the ball is highest, as a fraction of the distance it has to cover.
     * Half is a lob thrown and caught; earlier is a shot that leaps off the deck and spends the
     * rest of the flight coming down.
     */
    apex: number;
    /**
     * How pointed the arc is: the exponent on the climb. **Below 1** the ball is up almost at
     * once and hangs there, which reads as a mortar; **above 1** it stays low and swells only
     * near the top, which reads as a flat, fast shot that barely leaves the deck. At 1 it is a
     * plain sine hump — off the muzzle smoothly, over the top smoothly, back down the same way.
     */
    shape: number;
}

/**
 * A ball that reads as thrown rather than slid. The peak is a little over half again the muzzle
 * size, which is enough to see across a busy sea and not so much that a ball in flight competes
 * with the hull that fired it; the apex sits just before halfway because a shot leaves a gun
 * fast and arrives tired.
 */
export const SHOT_DEFAULTS: ShotSettings = {
    size: 18,
    arcHeight: 0.7,
    apex: 0.45,
    shape: 1
};

export const SHOT_FIELDS: SettingField<keyof ShotSettings>[] = [
    {
        key: "size",
        group: "The ball",
        label: "Ball size",
        unit: "px",
        min: 4,
        max: 80,
        step: 1,
        description:
            "How wide a ball is drawn as it leaves the gun, in world pixels — about a tenth of a starting hull. This is the size the arc grows from, so raising it raises the whole flight."
    },
    {
        key: "arcHeight",
        group: "The arc",
        label: "Arc height",
        unit: "x",
        min: 0,
        max: 3,
        step: 0.05,
        description:
            "How much bigger the ball is at the top of its arc, as a multiple of its muzzle size. This is the only cue a top-down sea has for height, so it is the dial that decides whether a shot reads as lobbed or as sliding across the water. Zero flies flat."
    },
    {
        key: "apex",
        group: "The arc",
        label: "Apex at",
        unit: "of the flight",
        min: 0,
        max: 1,
        step: 0.01,
        description:
            "Where along the flight the ball is highest. Half is an even lob; earlier gives a shot that leaps off the deck and spends the rest of its range falling, which is what a real gun looks like from above."
    },
    {
        key: "shape",
        group: "The arc",
        label: "Arc shape",
        unit: "",
        min: 0.2,
        max: 4,
        step: 0.05,
        description:
            "How pointed the arc is. Below 1 the ball is up almost immediately and hangs there — a mortar. Above 1 it stays low and swells only near the apex — a flat, fast shot. At 1 it is a plain sine hump."
    }
];

const SHOT_BY_KEY = indexFields(SHOT_FIELDS);

/** Anything at all, as a usable `ShotSettings`. See `coercePanel`. */
export function coerceShot(raw: unknown): ShotSettings {
    return coercePanel(SHOT_FIELDS, SHOT_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function shotField(key: keyof ShotSettings): SettingField<keyof ShotSettings> {
    return SHOT_BY_KEY.get(key)!;
}

/**
 * How big a ball is drawn at a given point in its flight, as a multiple of `size`.
 *
 * **The one implementation of the curve.** The game draws every ball through it and the
 * dashboard plots the same function, so what a designer judges on the preview is literally what
 * the arena shows — a second copy of this arithmetic in the editor is how a panel starts lying.
 *
 * `progress` is the fraction of the shot's **range** already covered, not of its lifetime in
 * seconds; the two are the same thing at a constant speed, and range is what the client is
 * already counting. It is normalised, so every gun arcs the same regardless of how far it
 * throws — a long gun's shot simply takes longer to get over the top. That is a decision and not
 * an oversight: an arc scaled by absolute range would make a short-barrelled swivel look like it
 * fired a pebble.
 *
 * The two halves are `min(rise, fall)`, which is what keeps the ends honest at every apex: at 0
 * the ball is already at the top as it leaves the gun and does nothing but fall, at 1 it climbs
 * the whole way and is cut off by the sea. Neither is a special case and neither divides by zero.
 */
export function shotScale(settings: ShotSettings, progress: number): number {
    if (settings.arcHeight <= 0) return 1;

    const along = Math.min(1, Math.max(0, progress));
    const rise = settings.apex <= 0 ? 1 : along / settings.apex;
    const fall = settings.apex >= 1 ? 1 : (1 - along) / (1 - settings.apex);
    const height = Math.min(1, rise, fall);

    // Sine rather than the bare fraction: it leaves the muzzle at a slope and rolls over the top
    // flat, which is what a thrown thing does. The exponent then leans that hump either way.
    return 1 + settings.arcHeight * Math.pow(Math.sin((height * Math.PI) / 2), settings.shape);
}

/** Shot numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface ShotDerived {
    /** How many times its muzzle size the ball is at the top of the arc. */
    peakScale: number;
    /** That size in world pixels — the number to judge against a hull. */
    peakSize: number;
    /** The same, as a fraction of a starting hull's length. Over 1 is a ball longer than a ship. */
    peakVsHull: number;
    /** How long the quoted gun's shot is in the air, in seconds. The clock the arc is spread over. */
    flightSeconds: number;
    /** How long after firing the ball is at its biggest, in seconds. */
    apexSeconds: number;
    /** How far from the muzzle that happens, in world pixels. */
    apexRange: number;
    /** Whether the arc is switched off entirely — a ball drawn one size the whole way. */
    flat: boolean;
}

/**
 * The arc priced against one gun's flight. The gun is passed in rather than read here for the
 * same reason `deriveWake` takes a cruise speed: this file holds no values from disk, and which
 * gun the dashboard quotes is a choice the panel makes.
 */
export function deriveShot(settings: ShotSettings, range: number, flightSeconds: number): ShotDerived {
    const peakScale = 1 + settings.arcHeight;

    return {
        peakScale,
        peakSize: settings.size * peakScale,
        peakVsHull: (settings.size * peakScale) / REFERENCE_HULL,
        flightSeconds,
        apexSeconds: flightSeconds * settings.apex,
        apexRange: range * settings.apex,
        flat: settings.arcHeight <= 0
    };
}

/**
 * The resident crews an arena sails itself, so a sea with two people in it still reads as a
 * battle. `config/bots.json`, and the design is design/Bots.md.
 *
 * **Read by the server only.** A bot is a hull in `ArenaState` like any other and a client draws
 * it like any other, so nothing here ever reaches the browser — which is why this is its own file
 * rather than eleven more keys in `GENERAL`, the same argument the ocean and the school make from
 * the other side.
 *
 * Two of these decide how many hulls exist and the other nine decide how one behaves, and the
 * split matters: `botPopulation` and `maxShips` are the only two the environment may override,
 * because they are the only two that anything outside the editor needs to talk a room out of.
 */
export interface BotSettings {
    /**
     * How many bots the sea carries, **whoever else is in it**. A count of bots and not a total:
     * captains add to the sea rather than displacing it, so a lone captain finds this many
     * opponents and twenty captains find this many on top of each other. **Zero turns bots off.**
     */
    botPopulation: number;
    /**
     * The ceiling on hulls in one room, and the only thing bots ever yield to. It is a budget
     * bots pay out of and never a reason to refuse a captain: `MAX_CLIENTS` is above it and stays
     * the seat cap.
     */
    maxShips: number;
    /** Fastest a discretionary arrival may come, in seconds. A wreck's replacement is not ramped. */
    joinIntervalSeconds: number;
    /** Fastest an eviction may happen, in seconds. Eviction is the backstop, not the mechanism. */
    leaveIntervalSeconds: number;
    /** How far from every captain a bot must be to arrive or be evicted, in world pixels. */
    despawnClearance: number;
    /** How long a departed captain's hull is held against the ceiling, in seconds. */
    humanGraceSeconds: number;
    /** How long before this room re-draws a personality it just lost, in seconds. */
    redrawCooldownSeconds: number;
    /**
     * How far a bot may read the sea, in world pixels. **A rule of play, not a budget** — a bot
     * that could read all of `state` would dodge a ball it has no way of seeing and sail at loot
     * across the map, which is a different game rather than a harder opponent.
     */
    perceptionRadius: number;
    /** How often a bot reconsiders, in Hz. It owes the room a frame every step but not a new one. */
    thinkHz: number;
    /** How far apart the best and worst bots are. Zero puts every bot at the midpoint. */
    skillSpread: number;
    /** What a burned-off bot spends of its own survival. Judgement only — never speed or turn. */
    recklessness: number;
}

/** The values the game ships with. Kept equal to `config/bots.json` — see `GENERAL_DEFAULTS`. */
export const BOTS_DEFAULTS: BotSettings = {
    botPopulation: 20,
    maxShips: 40,
    joinIntervalSeconds: 2,
    leaveIntervalSeconds: 4,
    despawnClearance: 1600,
    humanGraceSeconds: 6,
    redrawCooldownSeconds: 30,
    perceptionRadius: 2200,
    thinkHz: 6,
    skillSpread: 0.8,
    recklessness: 0.7
};

export const BOTS_FIELDS: SettingField<keyof BotSettings>[] = [
    {
        key: "botPopulation",
        group: "How many",
        label: "Bots in the sea",
        unit: "bots",
        min: 0,
        max: 50,
        step: 1,
        description:
            "How many bots the room carries, whoever else is in it. A count of bots and not a total: captains add to the sea rather than taking a seat out of it, so this is what a lone captain finds waiting and what twenty captains find on top of each other. Zero turns bots off entirely, which is what the test suite runs on."
    },
    {
        key: "maxShips",
        group: "How many",
        label: "Ship ceiling",
        unit: "hulls",
        min: 1,
        max: 50,
        step: 1,
        description:
            "The most hulls one room may hold, and the only thing bots ever give way to. It is a budget bots pay out of, never a reason to refuse a captain - the seat cap is higher, and by the time a room is that busy there are no bots left to argue with. Raising it costs every client in the room bandwidth, since a hull is patched to everybody."
    },
    {
        key: "joinIntervalSeconds",
        group: "Coming and going",
        label: "Join interval",
        unit: "s",
        min: 0,
        max: 30,
        step: 0.5,
        description:
            "Fastest a discretionary arrival may come. A room filling up should read as captains arriving rather than as a switch being thrown. It does not ration the hull a wreck left behind - a replacement is not an arrival, and rationing those is how a room drains under a heavy death rate."
    },
    {
        key: "leaveIntervalSeconds",
        group: "Coming and going",
        label: "Eviction interval",
        unit: "s",
        min: 0,
        max: 60,
        step: 0.5,
        description:
            "Fastest the capacity backstop may remove a bot. It should almost never run: a room over its ceiling normally gets hulls back by letting the surplus be sunk, which pays whoever sank them. An eviction destroys the plunder that hull was holding, so this is the last resort and a rising count of them is a bug rather than a statistic."
    },
    {
        key: "despawnClearance",
        group: "Coming and going",
        label: "Despawn clearance",
        unit: "px",
        min: 0,
        max: 6000,
        step: 50,
        description:
            "How far from every captain a bot has to be before it may arrive or be evicted. A boat somebody is looking at must never blink in or out. Judge it against what the camera actually shows rather than against the map size - the default clears a grown captain's view with room to spare."
    },
    {
        key: "humanGraceSeconds",
        group: "Coming and going",
        label: "Human grace",
        unit: "s",
        min: 0,
        max: 60,
        step: 1,
        description:
            "How long a departed captain's hull is held against the ceiling. The dominant population event in an arena game is a death, not an arrival, and a captain who just sank is usually about to come back - without this the room churns hardest exactly when it is fullest."
    },
    {
        key: "redrawCooldownSeconds",
        group: "Coming and going",
        label: "Redraw cooldown",
        unit: "s",
        min: 0,
        max: 600,
        step: 5,
        description:
            "How long before this room re-uses a personality it just lost. Seeing the name you sank sail straight back in is the most bot-like thing the design could do. It is a soft skip, though: a cooling name is used anyway if nothing else is free, because a visibly short sea is worse than a repeated one."
    },
    {
        key: "perceptionRadius",
        group: "Judgement",
        label: "Perception radius",
        unit: "px",
        min: 200,
        max: 6000,
        step: 50,
        description:
            "How far a bot may read the sea. A rule of play and not a performance budget: a bot that could read the whole arena would dodge a ball it has no way of seeing and sail at loot across the map, which is a different game rather than a harder opponent. The default is about twice the reach of a long gun."
    },
    {
        key: "thinkHz",
        group: "Judgement",
        label: "Think rate",
        unit: "Hz",
        min: 1,
        max: 30,
        step: 1,
        description:
            "How often a bot reconsiders. It owes the room a frame every step but not a new one, and repeating the last decision in between is exactly what a captain with late packets gets - so this is a fairness setting as much as a cost one. Raising it toward the input rate makes bots sharper and the step time spikier."
    },
    {
        key: "skillSpread",
        group: "Judgement",
        label: "Skill spread",
        unit: "x",
        min: 0,
        max: 1,
        step: 0.05,
        description:
            "How far apart the best and worst bots are. Zero plays every bot at the midpoint, which is consistent and forgettable; one uses the whole authored range, so some are worth avoiding and some are worth hunting. Skill buys judgement only - how far ahead a bot looks and how steady its aim - never speed, turn rate or reach."
    },
    {
        key: "recklessness",
        group: "Judgement",
        label: "Recklessness",
        unit: "x",
        min: 0,
        max: 1,
        step: 0.05,
        description:
            "What a bot spends of its own survival once the room has more hulls than it wants and it has been marked to burn off. It accepts less water, gives less berth and drives at things it can see will kill it - with its full lookahead intact, so it is throwing its life away rather than blundering. Zero makes a marked bot play normally and the surplus takes far longer to clear."
    }
];

const BOTS_BY_KEY = indexFields(BOTS_FIELDS);

/** Anything at all, as a usable `BotSettings`. See `coercePanel`. */
export function coerceBots(raw: unknown): BotSettings {
    return coercePanel(BOTS_FIELDS, BOTS_DEFAULTS, raw);
}

/** The field metadata for one key, for an editor row that needs its own bounds. */
export function botsField(key: keyof BotSettings): SettingField<keyof BotSettings> {
    return BOTS_BY_KEY.get(key)!;
}

/**
 * The **cross-field** clamps, which `coerceBots` cannot make and a slider cannot enforce.
 *
 * Each spans more than one setting, and three of them span a setting and a constant this file may
 * not import: `shared/Settings.ts` holds no values from disk and imports nothing, which is what
 * lets it compile for the browser, for Node and for the editor alike. So the arena's size, its
 * seat cap and its input rate are passed in, exactly as `deriveLoot` takes a map size.
 *
 * **The first two are ordered on purpose.** Clamping the population against an unclamped ceiling
 * would let one bad number through both: a file asking for ninety ships and ninety bots would come
 * out as fifty ships and ninety bots, which is the one combination that cannot be true.
 *
 * Idempotent, so running it twice costs nothing and a panel is free to run it on every keystroke.
 */
export function resolveBots(settings: BotSettings, mapSize: number, maxClients: number, inputHz: number): BotSettings {
    const maxShips = Math.min(settings.maxShips, maxClients);

    return {
        ...settings,
        maxShips,
        botPopulation: Math.min(settings.botPopulation, maxShips),
        despawnClearance: Math.min(settings.despawnClearance, mapSize / 3),
        perceptionRadius: Math.min(settings.perceptionRadius, mapSize / 2),
        thinkHz: Math.min(settings.thinkHz, inputHz)
    };
}

/**
 * Bytes one ship costs on the wire per patch, from the measurement in design/Networking.md, which
 * put a completely full arena of fifty at about 27 kB/s downstream at the 20 Hz patch rate.
 *
 * Copied rather than imported for the same reason `BARE_SHARE` above is copied: this file may not
 * import the modules that would know it, and a readout has to answer about the sliders rather than
 * about a saved file. It is an order-of-magnitude number and is used as one.
 */
const SHIP_PATCH_BYTES = 27;

/** Patches a second, from PATCH_RATE_MS in shared/Sailing.ts. Copied, as above. */
const PATCHES_PER_SECOND = 20;

/** Bot numbers that fall out of the stored ones. Not stored — see `GeneralDerived`. */
export interface BotsDerived {
    /** The settings after `resolveBots`, which is what the arena will actually run on. */
    effective: BotSettings;
    /** True when a clamp moved something, so the panel can say which slider is not being obeyed. */
    clamped: boolean;
    /** Hulls in the sea with one captain in it — the case the whole feature exists for. */
    hullsWithOneCaptain: number;
    /**
     * How many captains it takes before the room starts handing hulls back. Below this the bot
     * population is simply steady, which is the ordinary case and should stay boring.
     */
    captainsBeforeBurnOff: number;
    /** Downstream cost of a full room, per client, in bytes per second. */
    ceilingBytesPerSecond: number;
    /** Brain decisions a second across the whole room — the CPU term to watch. */
    thinksPerSecond: number;
    /** What a bot can see, as a share of the furthest anything can be on a torus this size. */
    perceptionShare: number;
    /** The despawn clearance in seconds of cruising, which is the legible form of a distance. */
    clearanceSeconds: number;
    /**
     * How many rooms one roster can crew at this population. `Infinity` until there is a roster to
     * divide — the roster arrives with the claims registry.
     */
    roomsSupplied: number;
}

/**
 * The readouts under the Bots panel. There is no live scene behind that panel — a population is
 * not something one client can show — so this arithmetic is the whole of what the dashboard adds
 * over editing the JSON by hand, and it is deliberately about **consequences** rather than
 * restatements: what a lone captain finds, when the room starts giving hulls back, what a full
 * room costs, and how much of the sea a bot is allowed to know about.
 */
export function deriveBots(
    settings: BotSettings,
    mapSize: number,
    maxClients: number,
    inputHz: number,
    cruiseSpeed: number,
    rosterSize = 0
): BotsDerived {
    const effective = resolveBots(settings, mapSize, maxClients, inputHz);
    const clamped = (Object.keys(effective) as (keyof BotSettings)[]).some((key) => effective[key] !== settings[key]);

    return {
        effective,
        clamped,
        hullsWithOneCaptain: Math.min(effective.maxShips, effective.botPopulation + 1),
        captainsBeforeBurnOff: effective.maxShips - effective.botPopulation + 1,
        ceilingBytesPerSecond: effective.maxShips * SHIP_PATCH_BYTES * PATCHES_PER_SECOND,
        thinksPerSecond: effective.botPopulation * effective.thinkHz,
        perceptionShare: effective.perceptionRadius / (mapSize / 2),
        clearanceSeconds: cruiseSpeed > 0 ? effective.despawnClearance / cruiseSpeed : Infinity,
        roomsSupplied: rosterSize > 0 && effective.botPopulation > 0 ? Math.floor(rosterSize / effective.botPopulation) : Infinity
    };
}
